import { Link } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Package, MapPin, Calendar, Ruler, Euro, Clock, ArrowRight, MessageSquare } from 'lucide-react';

const formatPrice = (min, max) => {
  if (min == null && max == null) return null;
  const fmt = (v) => `${Number(v).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
  if (min != null && max != null) return `${fmt(min)} – ${fmt(max)}`;
  if (min != null) return `ab ${fmt(min)}`;
  return `bis ${fmt(max)}`;
};

const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

function DetailRow({ icon: Icon, color, label, value }) {
  if (value == null || value === '') return null;
  return (
    <div className="flex items-start gap-3 py-2 border-b border-slate-100 dark:border-slate-800 last:border-0">
      <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${color}`} />
      <div className="flex-1 min-w-0">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="text-sm font-medium">{value}</div>
      </div>
    </div>
  );
}

export function WantedAdDetailDialog({ ad, open, onClose }) {
  if (!ad) return null;

  const priceStr = formatPrice(ad.price_min_eur, ad.price_max_eur);
  const widthStr =
    ad.width_min_mm != null || ad.width_max_mm != null
      ? `${ad.width_min_mm != null ? ad.width_min_mm : '?'} – ${ad.width_max_mm != null ? ad.width_max_mm : '?'} mm`
      : null;
  const locationStr = [ad.poster_postal_code, ad.poster_country].filter(Boolean).join(', ') || null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="wanted-ad-detail-dialog">
        <DialogHeader>
          <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 text-[10px] w-fit mb-1">
            {ad.category_name}
          </Badge>
          <DialogTitle className="text-lg leading-tight pr-6" data-testid="wanted-ad-detail-title">
            {ad.material_type_description}
          </DialogTitle>
        </DialogHeader>

        <div className="mt-1">
          <DetailRow icon={Package} color="text-blue-500" label="Mindestmenge" value={`${ad.min_quantity} ${ad.quantity_unit}`} />
          <DetailRow icon={Euro} color="text-emerald-600" label="Preisvorstellung" value={priceStr} />
          <DetailRow icon={Ruler} color="text-purple-500" label="Breite" value={widthStr} />
          <DetailRow icon={MapPin} color="text-green-500" label="Standort" value={locationStr} />
          <DetailRow icon={Calendar} color="text-orange-500" label="Gültig bis" value={formatDate(ad.valid_until)} />
          <DetailRow icon={Clock} color="text-slate-400" label="Eingestellt am" value={formatDate(ad.posted_at)} />
        </div>

        <div className="flex items-start gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/50 rounded-lg p-3 text-xs text-amber-800 dark:text-amber-300 mt-2">
          <MessageSquare className="h-4 w-4 mt-0.5 shrink-0" />
          <span>Möchten Sie ein passendes Material anbieten? Öffnen Sie das Gesuch im Marktplatz und reichen Sie ein strukturiertes Angebot ein.</span>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={onClose} data-testid="wanted-ad-detail-close-btn">
            Schließen
          </Button>
          <Button asChild className="bg-orange-500 hover:bg-orange-600 gap-1.5">
            <Link to={`/wanted?highlight=${ad.wanted_id}`} data-testid="wanted-ad-detail-open-platform-btn">
              Im Marktplatz ansehen <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
